import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, XCircle, Undo2, Upload, Play, Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';

// Gestor de Tráfego — aba Ações (Fases 3b/4/5): propostas de mudança na conta de anúncios (do agente, das regras
// ou manuais) que só um admin aprova; campanha nova é revisada aqui (textos, orçamento, imagens) e criada PAUSADA;
// histórico com desfazer; regras automáticas com teto.

const fmtBRL = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const fmtData = (s) => (s ? new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const erroMsg = (err) => err?.response?.data?.error || err?.message;

const TIPO = { pausar: 'Pausar', ativar: 'Ativar', orcamento: 'Mudar orçamento', criar_campanha: 'Criar campanha (pausada)' };
const ORIGEM = { agente: ['Gestor IA', 'bg-indigo-100 text-indigo-700'], regra: ['Regra', 'bg-amber-100 text-amber-700'], manual: ['Manual', 'bg-gray-100 text-gray-600'] };
const STATUS = {
  proposta: ['Aguardando', 'bg-blue-100 text-blue-700'],
  executando: ['Executando', 'bg-blue-100 text-blue-700'],
  executada: ['Executada', 'bg-green-100 text-green-700'],
  recusada: ['Recusada', 'bg-gray-100 text-gray-600'],
  erro: ['Erro', 'bg-red-100 text-red-700'],
  expirada: ['Expirada', 'bg-gray-100 text-gray-500'],
  desfeita: ['Desfeita', 'bg-purple-100 text-purple-700'],
};

function Pill({ par }) {
  const [label, cls] = par || ['—', 'bg-gray-100 text-gray-500'];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
}

function Avisos({ avisos }) {
  const a = avisos?.avisos || [];
  const b = avisos?.bloqueios || [];
  if (!a.length && !b.length) return null;
  return (
    <div className="space-y-1">
      {b.map((t, i) => <div key={`b${i}`} className="flex gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-1.5"><XCircle size={14} className="mt-0.5 shrink-0" /> {t}</div>)}
      {a.map((t, i) => <div key={`a${i}`} className="flex gap-2 text-xs text-yellow-800 bg-yellow-50 rounded-lg px-3 py-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {t}</div>)}
    </div>
  );
}

// ── revisão da campanha proposta ───────────────────────────────────────────
function CampanhaProposta({ acao, imagens, biblioteca, podeEditar }) {
  const qc = useQueryClient();
  const [p, setP] = useState(() => JSON.parse(JSON.stringify(acao.parametros || {})));
  const [sujo, setSujo] = useState(false);
  const mudar = (fn) => { setP((x) => { const y = JSON.parse(JSON.stringify(x)); fn(y); return y; }); setSujo(true); };
  const salvar = useMutation({
    mutationFn: () => api.put(`/trafego/acoes/${acao.id}`, { parametros: p }, { timeout: 120000 }),
    onSuccess: (r) => { setP(r.data.data.parametros); setSujo(false); qc.invalidateQueries({ queryKey: ['trafego-acoes'] }); },
    onError: (err) => alert('Não salvou: ' + erroMsg(err)),
  });
  const subir = useMutation({
    mutationFn: ({ i, file }) => { const fd = new FormData(); fd.append('imagem', file); fd.append('anuncio', i); return api.post(`/trafego/acoes/${acao.id}/imagem`, fd, { timeout: 120000 }); },
    onSuccess: (r) => { setP(r.data.data.acao.parametros); setSujo(false); qc.invalidateQueries({ queryKey: ['trafego-acoes'] }); qc.invalidateQueries({ queryKey: ['trafego-imagens'] }); },
    onError: (err) => alert('Não subiu a imagem: ' + erroMsg(err)),
  });
  const urlDe = (hash) => acao.imagens_url?.[hash] || imagens.find((i) => i.hash === hash)?.url;
  const crDe = (id) => acao.criativos?.[id] || biblioteca.find((c) => c.id === Number(id));
  const total = (p.conjuntos || []).reduce((s, c) => s + (Number(c.orcamento_diario) || 0), 0);

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-xs font-medium text-gray-600">Nome da campanha</span>
        <input className="input-field mt-1" value={p.nome || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.nome = e.target.value; })} />
      </label>
      <div className="text-xs text-gray-500">{p.tipo === 'formulario' ? 'Formulário instantâneo (lead cai no CRM e avisa no WhatsApp)' : 'Clique para WhatsApp (número do Iago)'} · {p.categoria_habitacao ? 'categoria Habitação' : 'sem categoria especial'} · total <strong>{fmtBRL(total)}/dia</strong></div>
      {p.tipo === 'formulario' && (
        <div className="border border-gray-100 rounded-lg p-3 text-xs space-y-2">
          <div className="font-medium text-gray-700">Formulário {p.formulario_id ? `(reaproveita o ${p.formulario_id})` : '(criado na página ao aprovar)'} — perguntas: nome, WhatsApp, e-mail, morar ou investir</div>
          {!p.formulario_id && (
            <>
              <label className="block"><span className="text-gray-500">Título da abertura</span>
                <input className="input-field mt-1" maxLength={60} value={p.formulario?.titulo || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.formulario = { ...(y.formulario || {}), titulo: e.target.value }; })} /></label>
              <label className="block"><span className="text-gray-500">Texto da abertura</span>
                <textarea rows={2} maxLength={300} className="input-field mt-1" value={p.formulario?.texto || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.formulario = { ...(y.formulario || {}), texto: e.target.value }; })} /></label>
              <label className="block"><span className="text-gray-500">Mensagem de agradecimento</span>
                <input className="input-field mt-1" maxLength={200} value={p.formulario?.agradecimento || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.formulario = { ...(y.formulario || {}), agradecimento: e.target.value }; })} /></label>
            </>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {(p.conjuntos || []).map((c, i) => (
          <div key={i} className="border border-gray-100 rounded-lg p-3 text-xs space-y-1">
            <div className="flex items-center justify-between gap-2">
              <strong className="text-sm text-gray-800">{i + 1}. {c.nome}</strong>
              <label className="flex items-center gap-1">R$
                <input type="number" min="6" step="1" className="border border-gray-200 rounded px-2 py-1 w-20 text-right" value={c.orcamento_diario}
                  disabled={!podeEditar} onChange={(e) => mudar((y) => { y.conjuntos[i].orcamento_diario = e.target.value; })} />/dia
              </label>
            </div>
            <div><span className="text-gray-500">Onde:</span> {(c.cidades || []).map((x) => `${x.nome} +${x.raio_km} km`).join(' · ')}</div>
            <div><span className="text-gray-500">Idade:</span> {c.idade_min}–{c.idade_max}{c.advantage_audience ? ' · público Advantage+' : ''}</div>
            {!!c.interesses?.length && <div><span className="text-gray-500">Interesses:</span> {c.interesses.map((x) => x.nome).join(', ')}</div>}
            {!!c.publicos_personalizados?.length && <div><span className="text-gray-500">Públicos personalizados:</span> {c.publicos_personalizados.length}</div>}
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {(p.anuncios || []).map((a, i) => (
          <div key={i} className="border border-gray-100 rounded-lg p-3 grid md:grid-cols-[140px_1fr] gap-3">
            <div className="space-y-2">
              {a.cartoes?.length ? (
                <div className="grid grid-cols-2 gap-1">
                  {a.cartoes.map((ct, k) => { const c = crDe(ct.criativo_id); return c?.miniatura
                    ? <img key={k} src={c.miniatura} alt="" title={`#${ct.criativo_id} ${c.nome}`} className="w-full aspect-square object-cover rounded border border-gray-100" />
                    : <div key={k} className="aspect-square rounded border border-dashed border-gray-300 text-[10px] p-1">#{ct.criativo_id}</div>; })}
                  <div className="col-span-2 text-[10px] text-gray-500 text-center">Carrossel · {a.cartoes.length} cartões</div>
                </div>
              ) : a.criativo_id ? (() => { const c = crDe(a.criativo_id); return (
                <div className="relative">
                  {c?.miniatura ? <img src={c.miniatura} alt="" className="w-full rounded border border-gray-100 object-cover aspect-square" /> : <div className="w-full aspect-square rounded border border-dashed border-gray-300 text-xs p-2">criativo #{a.criativo_id}</div>}
                  {c && <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1 rounded">{c.tipo === 'video' ? 'vídeo' : 'imagem'}{c.formato ? ` · ${c.formato}` : ''}</span>}
                </div>); })()
              : a.imagem_hash && urlDe(a.imagem_hash)
                ? <img src={urlDe(a.imagem_hash)} alt="" className="w-full rounded border border-gray-100 object-cover aspect-square" />
                : <div className="w-full aspect-square rounded border border-dashed border-red-300 bg-red-50 text-red-600 text-[11px] p-2 flex items-center text-center">{a.imagem_hash ? 'imagem da conta (sem prévia)' : `Falta imagem${a.imagem_descricao ? `: ${a.imagem_descricao}` : ''}`}</div>}
              {podeEditar && (
                <>
                  <label className="flex items-center justify-center gap-1 text-xs border border-gray-200 rounded px-2 py-1 cursor-pointer hover:bg-gray-50">
                    <Upload size={12} /> {subir.isPending ? 'Enviando…' : 'Subir imagem'}
                    <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => e.target.files[0] && subir.mutate({ i, file: e.target.files[0] })} />
                  </label>
                  <select className="w-full text-xs border border-gray-200 rounded px-1 py-1" value={a.criativo_id || ''}
                    onChange={(e) => mudar((y) => { y.anuncios[i].criativo_id = e.target.value ? Number(e.target.value) : null; if (e.target.value) { y.anuncios[i].imagem_hash = null; y.anuncios[i].cartoes = []; } })}>
                    <option value="">— da biblioteca (Criativos) —</option>
                    {biblioteca.map((c) => <option key={c.id} value={c.id}>#{c.id} {c.tipo === 'video' ? '🎬' : '🖼'} {c.nome.slice(0, 32)}{c.formato ? ` (${c.formato})` : ''}</option>)}
                  </select>
                  <select className="w-full text-xs border border-gray-200 rounded px-1 py-1" value={a.imagem_hash || ''} onChange={(e) => mudar((y) => { y.anuncios[i].imagem_hash = e.target.value || null; if (e.target.value) { y.anuncios[i].criativo_id = null; y.anuncios[i].cartoes = []; } })}>
                    <option value="">— imagem recente da conta —</option>
                    {imagens.map((im) => <option key={im.hash} value={im.hash}>{(im.nome || im.hash).slice(0, 40)}</option>)}
                  </select>
                </>
              )}
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex flex-wrap items-center gap-2"><strong className="text-sm text-gray-800">{a.nome}</strong>
                <span className="text-gray-500">em: {(a.conjuntos || []).map((k) => p.conjuntos[k]?.nome || `#${k + 1}`).join(' · ')}</span></div>
              <label className="block"><span className="text-gray-500">Título</span>
                <input className="input-field mt-1" value={a.titulo || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.anuncios[i].titulo = e.target.value; })} /></label>
              <label className="block"><span className="text-gray-500">Texto</span>
                <textarea rows={5} className="input-field mt-1" value={a.texto || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.anuncios[i].texto = e.target.value; })} /></label>
              {p.tipo !== 'formulario' && (
                <label className="block"><span className="text-gray-500">Mensagem que já vem escrita no WhatsApp do cliente</span>
                  <input className="input-field mt-1" value={a.mensagem_whatsapp || ''} disabled={!podeEditar} onChange={(e) => mudar((y) => { y.anuncios[i].mensagem_whatsapp = e.target.value; })} /></label>
              )}
            </div>
          </div>
        ))}
      </div>
      {podeEditar && sujo && (
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
          {salvar.isPending ? 'Conferindo na Meta…' : 'Salvar alterações'}
        </button>
      )}
    </div>
  );
}

function CardAcao({ acao, isAdmin, imagens, biblioteca }) {
  const qc = useQueryClient();
  const [novo, setNovo] = useState(acao.parametros?.novo_diario ?? '');
  const atualizar = () => { qc.invalidateQueries({ queryKey: ['trafego-acoes'] }); };
  const aprovar = useMutation({
    mutationFn: () => api.post(`/trafego/acoes/${acao.id}/aprovar`, {}, { timeout: 300000 }),
    onSuccess: atualizar,
    onError: (err) => { atualizar(); alert('Não executou: ' + erroMsg(err)); },
  });
  const recusar = useMutation({ mutationFn: () => api.post(`/trafego/acoes/${acao.id}/recusar`, {}), onSuccess: atualizar, onError: (err) => alert(erroMsg(err)) });
  const salvarOrc = useMutation({
    mutationFn: () => api.put(`/trafego/acoes/${acao.id}`, { parametros: { novo_diario: Number(novo) } }),
    onSuccess: atualizar, onError: (err) => alert('Não salvou: ' + erroMsg(err)),
  });
  const bloqueado = !!acao.avisos?.bloqueios?.length;
  const confirmar = () => {
    const txt = acao.tipo === 'criar_campanha'
      ? 'Criar a campanha na Meta? Ela nasce PAUSADA — para rodar, ative depois.'
      : `Executar agora na Meta: ${TIPO[acao.tipo]} — ${acao.resumo}?`;
    if (window.confirm(txt)) aprovar.mutate();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-gray-800">#{acao.id} · {TIPO[acao.tipo]}</span>
        <Pill par={ORIGEM[acao.origem]} />
        <span className="text-xs text-gray-400">{acao.proposta_por ? `${acao.proposta_por} · ` : ''}{fmtData(acao.created_at)} · vale até {fmtData(acao.expira_em)}</span>
      </div>
      <div className="text-sm text-gray-700">{acao.resumo}</div>
      {acao.motivo && <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 whitespace-pre-line">{acao.motivo}</div>}
      <Avisos avisos={acao.avisos} />
      {acao.tipo === 'orcamento' && (
        <div className="flex items-center gap-2 text-sm">
          De {fmtBRL(acao.parametros?.diario_antes)} para R$
          <input type="number" min="6" step="1" className="border border-gray-200 rounded px-2 py-1 w-24 text-right" value={novo} onChange={(e) => setNovo(e.target.value)} />/dia
          {Number(novo) !== Number(acao.parametros?.novo_diario) && <button onClick={() => salvarOrc.mutate()} className="text-xs border border-gray-200 rounded px-2 py-1">Salvar valor</button>}
        </div>
      )}
      {acao.tipo === 'criar_campanha' && <CampanhaProposta acao={acao} imagens={imagens} biblioteca={biblioteca} podeEditar />}
      {isAdmin ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={confirmar} disabled={aprovar.isPending || bloqueado}
            className="flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            title={bloqueado ? 'Resolva os itens em vermelho antes' : ''}>
            <CheckCircle2 size={15} /> {aprovar.isPending ? 'Executando na Meta…' : acao.tipo === 'criar_campanha' ? 'Aprovar e criar pausada' : 'Aprovar e executar'}
          </button>
          <button onClick={() => recusar.mutate()} disabled={recusar.isPending} className="flex items-center gap-1 border border-gray-200 px-4 py-2 rounded-lg text-sm"><XCircle size={15} /> Recusar</button>
        </div>
      ) : <p className="text-xs text-gray-500">Só um admin aprova.</p>}
    </div>
  );
}

// ── regras automáticas ─────────────────────────────────────────────────────
const METRICA = { investimento: 'Investimento (R$)', resultados: 'Conversas/leads (qtde)', custo_resultado: 'Custo por conversa/lead (R$)', ctr: 'CTR (%)' };
const ACAO = { pausar: 'Pausar', reduzir_orcamento: 'Reduzir orçamento', aumentar_orcamento: 'Aumentar orçamento' };
const NIVEL = { ad: 'Anúncio', adset: 'Conjunto', campaign: 'Campanha' };
const REGRA_NOVA = { nome: '', nivel: 'ad', metrica: 'resultados', operador: '<', valor: 1, janela_dias: 3, gasto_minimo: 60, acao: 'pausar', acao_pct: 20, modo: 'sugerir', ativa: false, filtro_campanha: '' };

function LinhaRegra({ regra, isAdmin, onFechar }) {
  const qc = useQueryClient();
  const [r, setR] = useState(regra);
  const set = (k) => (e) => setR((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const salvar = useMutation({
    mutationFn: () => (r.id ? api.put(`/trafego/regras/${r.id}`, r) : api.post('/trafego/regras', r)),
    onSuccess: (x) => { setR(x.data.data); qc.invalidateQueries({ queryKey: ['trafego-regras'] }); onFechar?.(); },
    onError: (err) => alert(erroMsg(err)),
  });
  const remover = useMutation({ mutationFn: () => api.delete(`/trafego/regras/${r.id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['trafego-regras'] }) });
  const sel = (k, opcoes) => <select className="border border-gray-200 rounded px-1 py-1" value={r[k]} disabled={!isAdmin} onChange={set(k)}>{Object.entries(opcoes).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  const num = (k, w = 'w-20') => <input type="number" className={`border border-gray-200 rounded px-2 py-1 text-right ${w}`} value={r[k] ?? ''} disabled={!isAdmin} onChange={set(k)} />;

  return (
    <div className={`border rounded-lg p-3 text-xs space-y-2 ${r.ativa ? 'border-green-200 bg-green-50/40' : 'border-gray-100 bg-white'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 font-semibold"><input type="checkbox" checked={!!r.ativa} disabled={!isAdmin} onChange={set('ativa')} /> Ligada</label>
        <input className="border border-gray-200 rounded px-2 py-1 flex-1 min-w-[200px] text-sm" placeholder="Nome da regra" value={r.nome} disabled={!isAdmin} onChange={set('nome')} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        Se {sel('nivel', NIVEL)} ativo gastou ≥ R$ {num('gasto_minimo')} nos últimos {num('janela_dias', 'w-14')} dias e {sel('metrica', METRICA)}
        {sel('operador', { '>': 'maior que', '<': 'menor que' })} {num('valor')} → {sel('acao', ACAO)}
        {r.acao !== 'pausar' && <>em {num('acao_pct', 'w-14')}%</>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        Só campanhas com <input className="border border-gray-200 rounded px-2 py-1 w-40" placeholder="(todas)" value={r.filtro_campanha || ''} disabled={!isAdmin} onChange={set('filtro_campanha')} /> no nome ·
        Modo {sel('modo', r.acao === 'aumentar_orcamento' ? { sugerir: 'Sugerir (vai p/ aprovação)' } : { sugerir: 'Sugerir (vai p/ aprovação)', automatico: 'Automático (executa sozinho)' })}
        {r.ultima_avaliacao && <span className="text-gray-400">· última avaliação {fmtData(r.ultima_avaliacao)}</span>}
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="bg-primary text-white px-3 py-1 rounded text-xs">{salvar.isPending ? 'Salvando…' : 'Salvar'}</button>
          {r.id ? <button onClick={() => window.confirm('Apagar esta regra?') && remover.mutate()} className="flex items-center gap-1 border border-gray-200 px-3 py-1 rounded text-xs"><Trash2 size={12} /> Apagar</button>
            : <button onClick={onFechar} className="border border-gray-200 px-3 py-1 rounded text-xs">Cancelar</button>}
        </div>
      )}
    </div>
  );
}

function Regras({ isAdmin, limites }) {
  const { data } = useQuery({ queryKey: ['trafego-regras'], queryFn: () => api.get('/trafego/regras').then((r) => r.data.data) });
  const [nova, setNova] = useState(false);
  const qc = useQueryClient();
  const avaliar = useMutation({
    mutationFn: () => api.post('/trafego/regras/avaliar', {}, { timeout: 300000 }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['trafego-acoes'] }); qc.invalidateQueries({ queryKey: ['trafego-regras'] }); const d = r.data.data;
      alert(d.pulado ? d.motivo : `${d.regras} regra(s) ligada(s): ${d.propostas} proposta(s) nova(s), ${d.automaticas} execução(ões) automática(s).`); },
    onError: (err) => alert(erroMsg(err)),
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-gray-800">Regras automáticas</h3>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${limites?.automacao_ligada ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          Automação geral {limites?.automacao_ligada ? 'LIGADA' : 'DESLIGADA'}
        </span>
        {isAdmin && <button onClick={() => avaliar.mutate()} disabled={avaliar.isPending} className="flex items-center gap-1 border border-gray-200 px-3 py-1.5 rounded-lg text-xs"><Play size={12} /> {avaliar.isPending ? 'Avaliando…' : 'Avaliar agora'}</button>}
        {isAdmin && !nova && <button onClick={() => setNova(true)} className="flex items-center gap-1 border border-gray-200 px-3 py-1.5 rounded-lg text-xs"><Plus size={12} /> Nova regra</button>}
      </div>
      <p className="text-xs text-gray-500">Rodam às 7h, 12h e 17h sobre o que está ativo. Em "Sugerir" viram proposta aqui em cima. "Automático" só vale com a automação geral ligada (Configuração), só pausa ou reduz orçamento, no máximo algumas vezes por dia, e dá para desfazer. Aumentar orçamento sempre pede aprovação e respeita o teto.</p>
      {nova && <LinhaRegra regra={REGRA_NOVA} isAdmin={isAdmin} onFechar={() => setNova(false)} />}
      {(data?.regras || []).map((r) => <LinhaRegra key={r.id} regra={r} isAdmin={isAdmin} />)}
    </div>
  );
}

// ── aba ────────────────────────────────────────────────────────────────────
export default function Acoes({ isAdmin }) {
  const qc = useQueryClient();
  const pend = useQuery({ queryKey: ['trafego-acoes', 'pendentes'], queryFn: () => api.get('/trafego/acoes?status=pendentes').then((r) => r.data.data) });
  const hist = useQuery({ queryKey: ['trafego-acoes', 'todas'], queryFn: () => api.get('/trafego/acoes').then((r) => r.data.data) });
  const temCampanha = (pend.data?.acoes || []).some((a) => a.tipo === 'criar_campanha');
  const imgs = useQuery({ queryKey: ['trafego-imagens'], enabled: temCampanha, queryFn: () => api.get('/trafego/meta/imagens').then((r) => r.data.data) });
  const bib = useQuery({ queryKey: ['trafego-criativos', 'todos'], enabled: temCampanha, queryFn: () => api.get('/trafego/criativos').then((r) => r.data.data.criativos) });
  const desfazer = useMutation({
    mutationFn: (id) => api.post(`/trafego/acoes/${id}/desfazer`, {}, { timeout: 120000 }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trafego-acoes'] }),
    onError: (err) => alert('Não desfez: ' + erroMsg(err)),
  });
  const lim = pend.data?.limites;
  const problemas = lim?.conta?.problemas || [];
  const historico = (hist.data?.acoes || []).filter((a) => a.status !== 'proposta');

  return (
    <div className="space-y-6">
      {problemas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
          <div className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> A conta de anúncios não está entregando</div>
          <ul className="list-disc pl-6 mt-1">{problemas.map((p, i) => <li key={i}>{p}</li>)}</ul>
          <p className="text-xs mt-1">Isso se resolve no Gerenciador de Anúncios da Meta (Cobrança). O CRM só avisa.</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Orçamento ativo agora: <strong>{lim?.orcamento_ativo != null ? `${fmtBRL(lim.orcamento_ativo)}/dia` : '—'}</strong></span>
        <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Teto da conta: <strong>{lim?.teto_orcamento_diario ? `${fmtBRL(lim.teto_orcamento_diario)}/dia` : 'não definido'}</strong></span>
        <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Maior mudança por vez: <strong>{lim?.max_variacao_pct ?? 30}%</strong></span>
        {lim?.conta?.saldo != null && <span className="bg-white border border-gray-100 rounded-lg px-3 py-1">Saldo pré-pago: <strong>{fmtBRL(lim.conta.saldo)}</strong></span>}
      </div>

      <div className="space-y-3">
        <h3 className="font-semibold text-gray-800">Aguardando aprovação</h3>
        {pend.isLoading && <div className="text-gray-500 text-sm">Carregando…</div>}
        {!pend.isLoading && !pend.data?.acoes?.length && <div className="bg-white rounded-xl border border-gray-100 p-5 text-sm text-gray-500">Nada pendente. Peça ao Gestor IA (ex.: "monte uma campanha do Lumière") ou ligue uma regra abaixo.</div>}
        {(pend.data?.acoes || []).map((a) => <CardAcao key={a.id} acao={a} isAdmin={isAdmin} imagens={imgs.data || []} biblioteca={bib.data || []} />)}
      </div>

      <Regras isAdmin={isAdmin} limites={lim} />

      <div className="space-y-2">
        <h3 className="font-semibold text-gray-800">Histórico</h3>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-2">Quando</th><th className="text-left px-3">Ação</th><th className="text-left px-3">Origem</th><th className="text-left px-3">Status</th><th className="text-left px-3">Quem decidiu</th><th className="px-4" /></tr>
            </thead>
            <tbody>
              {historico.map((a) => (
                <tr key={a.id} className="border-t border-gray-50 align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-xs">{fmtData(a.executado_em || a.decidido_em || a.created_at)}</td>
                  <td className="px-3 py-2"><div className="font-medium">#{a.id} {TIPO[a.tipo]}</div><div className="text-xs text-gray-500">{a.resumo}</div>
                    {a.resultado?.erro && <div className="text-xs text-red-600 mt-1">{a.resultado.erro}</div>}
                    {a.resultado?.campaign_id && <div className="text-xs text-gray-400">campanha {a.resultado.campaign_id} criada pausada</div>}</td>
                  <td className="px-3 py-2"><Pill par={ORIGEM[a.origem]} /></td>
                  <td className="px-3 py-2"><Pill par={STATUS[a.status]} /></td>
                  <td className="px-3 py-2 text-xs">{a.decidido_por_nome || (a.origem === 'regra' && a.executado_em ? 'automático' : '—')}</td>
                  <td className="px-4 py-2 text-right">
                    {isAdmin && a.status === 'executada' && (
                      <button onClick={() => window.confirm('Desfazer esta ação na Meta?') && desfazer.mutate(a.id)} disabled={desfazer.isPending}
                        className="flex items-center gap-1 border border-gray-200 px-2 py-1 rounded text-xs"><Undo2 size={12} /> Desfazer</button>
                    )}
                  </td>
                </tr>
              ))}
              {!historico.length && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Nenhuma ação decidida ainda.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
